import { usePage } from '@inertiajs/react';
import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  TextInput,
  Tooltip,
  Transition,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useDisclosure, useHover } from '@mantine/hooks';
import { IconAt, IconBrandWhatsapp, IconMessageChatbot, IconPhoneCall } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import { FormProvider } from '@/components/form-context.js';
import { PhoneRegionPicker } from '@/components/PhoneRegionPicker.jsx';
import useInertiaForm from '@/components/useInertiaForm.jsx';
import useLoading from '@/components/useLoading.jsx';

export default function BookACallPopup({ showFooter = true }) {
  if (!showFooter) {
    return null;
  }

  // book a call popup
  const [bookACallPopupState, bookACallPopupStateHandler] = useDisclosure(false);

  const user = usePage().props?.auth?.user;

  const loading = useLoading();

  const initialValues = useMemo(
    () => ({
      name: user?.full_name || '',
      email: user?.email || '',
      phone: user?.phone_without_code || '',
      phone_region: user?.phone_region || '',
      profession: '',
      scheduled_at: dayjs().add(1, 'hour').startOf('hour').startOf('minute').startOf('second').startOf('milliseconds').toDate(),
      policy: false,
    }),
    [],
  );

  const errors = useMemo(() => ({}), []);

  const form = useInertiaForm(
    {
      initialValues: initialValues,
      validate: {
        name: value => (value ? null : 'Name is required'),
        email: value => (value ? null : 'Email is required'),
        phone: value => (value ? null : 'Phone is required'),
        phone_region: value => (value ? null : 'Phone Region is required'),
        policy: value => (value ? null : 'The privacy policy must be accepted'),
      },
      validateInputOnBlur: true,
    },
    errors,
    route('site.book-a-call'),
    'Your call has ben scheduled successfully. We will contact you soon.',
    false,
    {
      successCallback: () => {
        bookACallPopupStateHandler.close();
      },
    },
  );

  return (
    <>
      <Modal
        withCloseButton={true}
        size={'md'}
        padding={'lg'}
        title="Book a Call"
        opened={bookACallPopupState}
        onClose={bookACallPopupStateHandler.close}
        closeOnEscape={false}
      >
        <FormProvider form={form}>
          <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: 'sm', blur: 2 }} />
          <form onSubmit={e => e.preventDefault()}>
            <TextInput label="Name Surname" placeholder="Your name" data-autofocus {...form.getInputProps('name')} />
            <TextInput
              type={'email'}
              label="E-mail"
              rightSectionPointerEvents="none"
              rightSection={<IconAt size={16} />}
              placeholder="Your email"
              mt={'sm'}
              {...form.getInputProps('email')}
            />
            <Group align={'center'} mt={'sm'}>
              <PhoneRegionPicker
                label="Phone Region"
                placeholder={'Your Country Code'}
                style={{ flex: 1 }}
                {...form.getInputProps('phone_region')}
              />
              <TextInput style={{ flex: 1 }} label="Phone" placeholder="Your phone number" {...form.getInputProps('phone')} />
            </Group>
            <TextInput label="Profession" placeholder="Your profession" mt={'sm'} {...form.getInputProps('profession')} />
            <DateTimePicker
              label="Pick date and time"
              clearable
              dropdownType="modal"
              placeholder="Pick date and time"
              minDate={new Date()}
              valueFormat={'DD.MM.YYYY @ HH:mm'}
              mt={'sm'}
              {...form.getInputProps('scheduled_at')}
            />

            <Checkbox
              {...form.getInputProps('policy', { type: 'checkbox' })}
              label="I agree to the processing of my personal data in accordance with the Privacy Policy."
              mt={'md'}
            />

            <Button c={'black'} size="md" fullWidth method={'post'} onClick={form.handleSubmit} mt={'lg'}>
              Book a Call
            </Button>
          </form>
        </FormProvider>
      </Modal>

      <ContactActions bookACallPopupStateHandler={bookACallPopupStateHandler} />
    </>
  );
}

function ContactActions({ bookACallPopupStateHandler }) {
  const { hovered, ref } = useHover();

  return (
    <>
      <Stack style={{ position: 'fixed', bottom: 30, right: 30, zIndex: 101 }} ref={ref}>
        <Transition transition={'fade-up'} mounted={hovered} exitDelay={300}>
          {transitionStyle => (
            <Stack style={transitionStyle}>
              <Box>
                <Tooltip label={'WhatsApp'} position="left" color={'black'}>
                  <ActionIcon
                    color={'green'}
                    variant="filled"
                    radius="xl"
                    size={'50'}
                    aria-label="WhatsApp"
                    component="a"
                    target={'_blank'}
                    href={'https://wa.me/message/WJ3XPXVN5XMAO1'}
                    c={'white'}
                  >
                    <IconBrandWhatsapp stroke={2} />
                  </ActionIcon>
                </Tooltip>
              </Box>

              <Box>
                <Tooltip label={'Book a call'} position="left" color={'black'}>
                  <ActionIcon
                    onClick={bookACallPopupStateHandler.open}
                    color={'blue'}
                    variant="filled"
                    radius="xl"
                    size={'50'}
                    aria-label="Book a call"
                  >
                    <IconPhoneCall stroke={2} />
                  </ActionIcon>
                </Tooltip>
              </Box>
            </Stack>
          )}
        </Transition>

        <Box>
          <ActionIcon variant="filled" radius="xl" size={'50'} aria-label="Book a call">
            <IconMessageChatbot stroke={2} />
          </ActionIcon>
        </Box>
      </Stack>
    </>
  );
}
